import React, { createContext, useContext, useRef, useState } from "react";
import {
  Invoice,
  InvoiceItem,
  newInvoice,
  newInvoiceItem,
} from "../../../models/invoice";
import Establishment from "../../../models/establishment";
import DispatchPoint from "../../../models/dispatch-point";
import Stamp from "../../../models/stamp";
import Client from "../../../models/client";
import Errors from "../../../models/errors";
import { PYG } from "../../../currency/available-currencies";
import moment from "moment";
import { changeTZToUtc } from "../../../utils/dates";

interface InvoiceFormContextValue {
  // Refs and State
  invoiceRef: React.MutableRefObject<Invoice | null>;
  invoiceDate: Date | undefined;
  saleType: string | undefined;
  currency: string;
  pygExchangeRate: number | undefined;
  totalAmount: number | undefined;
  amount10Percent: number | undefined;
  amount5Percent: number | undefined;
  amountExempt: number | undefined;
  iva10Percent: number | undefined;
  iva5Percent: number | undefined;
  applyDiscount: boolean;
  globalDiscount: number | undefined;
  establishment: Establishment | null;
  dispatchPoint: DispatchPoint | null;
  stamp: Stamp | null;
  client: Client | null;
  items: InvoiceItem[];
  errors: Errors;
  readonly?: boolean;

  // Handlers
  setInvoiceDate: (date: Date | undefined) => void;
  setSaleType: (type: string | undefined) => void;
  setCurrency: (currency: string) => void;
  setPygExchangeRate: (rate: number | undefined) => void;
  setApplyDiscount: (apply: boolean) => void;
  setEstablishment: (est: Establishment | null) => void;
  setDispatchPoint: (dp: DispatchPoint | null) => void;
  setStamp: (stamp: Stamp | null) => void;
  setClient: (client: Client | null) => void;
  setItems: (items: InvoiceItem[]) => void;
  setErrors: (errors: Errors) => void;

  onEstablishmentChange: (newEstablishment: Establishment | null) => void;
  onDispatchPointChange: (newDispatchPoint: DispatchPoint | null) => void;
  onStampChange: (newStamp: Stamp | null) => void;
  onClientChange: (newClient: Client | null) => void;
  onInvoiceDateChange: (value: string | moment.Moment) => void;
  onSaleTypeChange: (newSaleType: string) => void;
  onTransactionTypeCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCurrencyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPygExchangeRateChange: (newExchangeRate?: number) => void;
  onApplyDiscountChange: (applyDiscount: boolean) => void;
  onGlobalDiscountChange: (newGlobalDiscount?: number) => void;
  onTermChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractModalityChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onDncpContractEntityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractYearChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractSequenceChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onDncpContractDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onObsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onItemChange: (index: number, item: InvoiceItem) => void;
  onItemDelete: (index: number) => void;
  onItemsChange: (newItems: InvoiceItem[]) => void;
  onAddItemClick: () => void;
}

const InvoiceFormContext = createContext<InvoiceFormContextValue | undefined>(
  undefined
);

export const useInvoiceFormContext = () => {
  const context = useContext(InvoiceFormContext);
  if (!context) {
    throw new Error(
      "useInvoiceFormContext must be used within InvoiceFormProvider"
    );
  }
  return context;
};

interface InvoiceFormProviderProps {
  children: React.ReactNode;
  taxPayerId?: number;
  readonly?: boolean;
}

export const InvoiceFormProvider: React.FC<InvoiceFormProviderProps> = ({
  children,
  taxPayerId,
  readonly,
}) => {
  const invoiceRef = useRef<Invoice | null>(newInvoice(taxPayerId));
  const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(undefined);
  const [saleType, setSaleType] = useState<string | undefined>(undefined);
  const [currency, setCurrency] = useState(PYG);
  const [pygExchangeRate, setPygExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [amount10Percent, setAmount10Percent] = useState<number | undefined>(
    undefined
  );
  const [amount5Percent, setAmount5Percent] = useState<number | undefined>(
    undefined
  );
  const [amountExempt, setAmountExempt] = useState<number | undefined>(
    undefined
  );
  const [iva10Percent, setIva10Percent] = useState<number | undefined>(
    undefined
  );
  const [iva5Percent, setIva5Percent] = useState<number | undefined>(undefined);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [globalDiscount, setGlobalDiscount] = useState<number | undefined>(
    undefined
  );
  const [establishment, setEstablishment] = useState<Establishment | null>(
    null
  );
  const [dispatchPoint, setDispatchPoint] = useState<DispatchPoint | null>(
    null
  );
  const [stamp, setStamp] = useState<Stamp | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [errors, setErrors] = useState<Errors>({});

  const onEstablishmentChange = (newEstablishment: Establishment | null) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.establishmentId = newEstablishment?.id;
    invoiceRef.current.establishmentCode = newEstablishment?.code;
    setEstablishment(newEstablishment);
  };

  const onDispatchPointChange = (newDispatchPoint: DispatchPoint | null) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.dispatchPointId = newDispatchPoint?.id;
    invoiceRef.current.dispatchPointIdentifier = newDispatchPoint?.identifier;
    setDispatchPoint(newDispatchPoint);
  };

  const onStampChange = (newStamp: Stamp | null) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.stampId = newStamp?.id;
    invoiceRef.current.stampIdentifier = newStamp?.identifier;
    invoiceRef.current.stampBeginDate = newStamp?.beginDate;
    setStamp(newStamp);
  };

  const onClientChange = (newClient: Client | null) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.clientId = newClient?.id;
    invoiceRef.current.clientCode = newClient?.code;
    invoiceRef.current.clientIsTaxPayer = newClient?.isTaxPayer;
    invoiceRef.current.clientRuc = newClient?.ruc;
    invoiceRef.current.clientSocialName = newClient?.socialName;
    invoiceRef.current.clientFantasyName = newClient?.fantasyName;
    invoiceRef.current.clientType = newClient?.type;
    invoiceRef.current.clientCountryCode = newClient?.countryCode;
    invoiceRef.current.clientHasAddress = newClient?.hasAddress;
    invoiceRef.current.clientAddress = newClient?.address;
    invoiceRef.current.clientHouseNumber = newClient?.houseNumber;
    invoiceRef.current.clientDepartment = newClient?.department;
    invoiceRef.current.clientDepartmentDescription =
      newClient?.departmentDescription;
    invoiceRef.current.clientDistrict = newClient?.district;
    invoiceRef.current.clientDistrictDescription =
      newClient?.districtDescription;
    invoiceRef.current.clientCity = newClient?.city;
    invoiceRef.current.clientCityDescription = newClient?.cityDescription;
    invoiceRef.current.clientPhoneNumber = newClient?.phoneNumber;
    invoiceRef.current.clientEmail = newClient?.email;
    setClient(newClient);
  };

  const onInvoiceDateChange = (value: string | moment.Moment) => {
    if (invoiceRef.current === null) return;
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    const invoiceDate = new Date((value as moment.Moment).toISOString());
    setInvoiceDate(invoiceDate);
    invoiceRef.current.invoiceDate = changeTZToUtc(invoiceDate).toISOString();
  };

  const onSaleTypeChange = (newSaleType: string) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.saleType = newSaleType;
    setSaleType(newSaleType);
  };

  const onTransactionTypeCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (invoiceRef.current === null) return;
    if (!isNaN(parseInt(e.target.value))) {
      invoiceRef.current.transactionTypeCode = parseInt(e.target.value);
    } else {
      invoiceRef.current.transactionTypeCode = undefined;
    }
  };

  const onCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.currency = e.target.value;
    setCurrency(e.target.value);
  };

  const onPygExchangeRateChange = (newExchangeRate?: number) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.pygExchangeRate = newExchangeRate;
    setPygExchangeRate(newExchangeRate);
  };

  const onApplyDiscountChange = (applyDiscount: boolean) => {
    if (invoiceRef.current === null) return;
    setApplyDiscount(applyDiscount);
  };

  const onGlobalDiscountChange = (newGlobalDiscount?: number) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.globalDiscount = newGlobalDiscount;
    setGlobalDiscount(newGlobalDiscount);
  };

  const onTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.term = e.target.value;
  };

  const onDncpContractCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.dncpContractCode = e.target.value;
  };

  const onDncpContractModalityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.dncpContractModality = e.target.value;
  };

  const onDncpContractEntityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.dncpContractEntity = e.target.value;
  };

  const onDncpContractYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.dncpContractYear = e.target.value;
  };

  const onDncpContractSequenceChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.dncpContractSequence = e.target.value;
  };

  const onDncpContractDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.dncpContractDate = e.target.value;
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (invoiceRef.current === null) return;
    invoiceRef.current.obs = e.target.value;
  };

  const onItemChange = (index: number, item: InvoiceItem) => {
    if (invoiceRef.current === null) return;
    const newItems = [...invoiceRef.current.items];
    newItems[index] = item;
    onItemsChange(newItems);
  };

  const onItemDelete = (index: number) => {
    if (invoiceRef.current === null) return;
    const newItems = [...invoiceRef.current.items];
    newItems.splice(index, 1);
    onItemsChange(newItems);
  };

  const onItemsChange = (newItems: InvoiceItem[]) => {
    if (invoiceRef.current === null) return;

    let newTotalAmount: number | undefined = undefined;
    let newAmount10Percent: number | undefined = undefined;
    let newAmount5Percent: number | undefined = undefined;
    let newAmountExempt: number | undefined = undefined;
    let newTaxed10Percent: number | undefined = undefined;
    let newTaxed5Percent: number | undefined = undefined;
    let newIva10Percent: number | undefined = undefined;
    let newIva5Percent: number | undefined = undefined;

    for (let item of newItems) {
      if (item.unitAmount !== undefined && item.quantity !== undefined) {
        newTotalAmount = newTotalAmount
          ? newTotalAmount + item.unitAmount * item.quantity
          : item.unitAmount * item.quantity;
      }

      if (item.amount10Percent !== undefined) {
        newAmount10Percent = newAmount10Percent
          ? newAmount10Percent + item.amount10Percent
          : item.amount10Percent;
      }

      if (item.amount5Percent !== undefined) {
        newAmount5Percent = newAmount5Percent
          ? newAmount5Percent + item.amount5Percent
          : item.amount5Percent;
      }

      if (item.amountExempt !== undefined) {
        newAmountExempt = newAmountExempt
          ? newAmountExempt + item.amountExempt
          : item.amountExempt;
      }

      if (item.taxed10Percent !== undefined) {
        newTaxed10Percent = newTaxed10Percent
          ? newTaxed10Percent + item.taxed10Percent
          : item.taxed10Percent;
      }

      if (item.taxed5Percent !== undefined) {
        newTaxed5Percent = newTaxed5Percent
          ? newTaxed5Percent + item.taxed5Percent
          : item.taxed5Percent;
      }

      if (item.iva10Percent !== undefined) {
        newIva10Percent = newIva10Percent
          ? newIva10Percent + item.iva10Percent
          : item.iva10Percent;
      }

      if (item.iva5Percent !== undefined) {
        newIva5Percent = newIva5Percent
          ? newIva5Percent + item.iva5Percent
          : item.iva5Percent;
      }
    }

    setTotalAmount(newTotalAmount);
    setAmount10Percent(newAmount10Percent);
    setAmount5Percent(newAmount5Percent);
    setAmountExempt(newAmountExempt);
    setIva10Percent(newIva10Percent);
    setIva5Percent(newIva5Percent);

    invoiceRef.current = {
      ...invoiceRef.current,
      items: newItems,
      amount: newTotalAmount,
      amount10Percent: newAmount10Percent,
      amount5Percent: newAmount5Percent,
      amountExempt: newAmountExempt,
      taxed10Percent: newTaxed10Percent,
      taxed5Percent: newTaxed5Percent,
      iva10Percent: newIva10Percent,
      iva5Percent: newIva5Percent,
    };

    setItems(newItems);
  };

  const onAddItemClick = () => {
    if (invoiceRef.current === null) return;
    const newItems = [...invoiceRef.current.items, newInvoiceItem()];
    onItemsChange(newItems);
  };

  const value: InvoiceFormContextValue = {
    invoiceRef,
    invoiceDate,
    saleType,
    currency,
    pygExchangeRate,
    totalAmount,
    amount10Percent,
    amount5Percent,
    amountExempt,
    iva10Percent,
    iva5Percent,
    applyDiscount,
    globalDiscount,
    establishment,
    dispatchPoint,
    stamp,
    client,
    items,
    errors,
    readonly,
    setInvoiceDate,
    setSaleType,
    setCurrency,
    setPygExchangeRate,
    setApplyDiscount,
    setEstablishment,
    setDispatchPoint,
    setStamp,
    setClient,
    setItems,
    setErrors,
    onEstablishmentChange,
    onDispatchPointChange,
    onStampChange,
    onClientChange,
    onInvoiceDateChange,
    onSaleTypeChange,
    onTransactionTypeCodeChange,
    onCurrencyChange,
    onPygExchangeRateChange,
    onApplyDiscountChange,
    onGlobalDiscountChange,
    onTermChange,
    onDncpContractCodeChange,
    onDncpContractModalityChange,
    onDncpContractEntityChange,
    onDncpContractYearChange,
    onDncpContractSequenceChange,
    onDncpContractDateChange,
    onObsChange,
    onItemChange,
    onItemDelete,
    onItemsChange,
    onAddItemClick,
  };

  return (
    <InvoiceFormContext.Provider value={value}>
      {children}
    </InvoiceFormContext.Provider>
  );
};
